import type { CategoryModel } from '@/api/generated/model/categoryModel';
import type { GetInteractionAggregatesReqGroupBy, ProjectAggregate } from '@/api/generated/model';
import { BUILT_IN_AGGREGATE_CATEGORY_NAMES } from '../../../hooks/useGroupByItems.utils';

const CUSTOM_AGGREGATE_PREFIX = 'custom_aggregate:';

export type MoveDestinationKind = 'topic' | 'model_suggestion' | 'action' | 'category';

export type AggregateQueryAxis = {
  categoryName: string;
  groupBy: GetInteractionAggregatesReqGroupBy;
  filterColumn: string;
  /** Kind sent to move-categories / custom-categories APIs */
  moveKind: string;
};

export type AggregateMovePath = {
  /** Full path including the leaf being moved (e.g. topic → sub_topic → action) */
  nodes: ProjectAggregate[];
  /** Destination parents that must be selected progressively (everything except the leaf) */
  destinationSteps: ProjectAggregate[];
  leaf: ProjectAggregate;
  immediateParent: ProjectAggregate;
};

const BUILTIN_GROUP_BY: Record<string, GetInteractionAggregatesReqGroupBy['kind']> = {
  topic: 'topic',
  sub_topic: 'category',
  category: 'category',
  action: 'user_action',
  user_action: 'user_action',
  model_suggestion: 'type_of_problem_v2',
  model_issue: 'type_of_problem_v2',
};

const BUILTIN_FILTER_COLUMN: Record<string, string> = {
  topic: 'topic',
  sub_topic: 'category',
  category: 'category',
  action: 'user_action',
  user_action: 'user_action',
  model_suggestion: 'type_of_problem_v2',
  model_issue: 'type_of_problem_v2',
};

const BUILTIN_MOVE_KIND: Record<string, string> = {
  topic: 'topic',
  sub_topic: 'sub_topic',
  category: 'sub_topic',
  action: 'action',
  user_action: 'action',
  model_suggestion: 'model_suggestion',
  model_issue: 'model_suggestion',
};

/** Leaf category names that action-mode moves reparent */
const ACTION_LEAF_NAMES = new Set(['action', 'user_action']);

function findAggregateByCategoryName(
  aggregates: ProjectAggregate[] | undefined,
  categoryName: string,
): ProjectAggregate | undefined {
  if (!aggregates?.length) return undefined;

  for (const node of aggregates) {
    if (node.category_name === categoryName) return node;
    const nested = findAggregateByCategoryName(node.children, categoryName);
    if (nested) return nested;
  }

  return undefined;
}

/**
 * DFS for a path from `root` to a leaf whose category_name is in `leafNames`.
 * Returns the first path found (root → … → leaf).
 */
function findPathToLeaf(root: ProjectAggregate, leafNames: Set<string>): ProjectAggregate[] | undefined {
  const visit = (node: ProjectAggregate, trail: ProjectAggregate[]): ProjectAggregate[] | undefined => {
    const next = [...trail, node];
    if (leafNames.has(node.category_name)) {
      return next;
    }
    for (const child of node.children ?? []) {
      const found = visit(child, next);
      if (found) return found;
    }
    return undefined;
  };

  return visit(root, []);
}

/**
 * Resolve the configured aggregate path for moving a leaf under the selected destination kind.
 * Returns undefined when configuration is missing or no path exists (caller should fall back).
 */
export function resolveAggregateMovePath(
  aggregates: ProjectAggregate[] | undefined,
  selectedKind: MoveDestinationKind,
  leafNames: Set<string> = ACTION_LEAF_NAMES,
): AggregateMovePath | undefined {
  if (!aggregates?.length) return undefined;

  // Destination kinds that are not tree roots in project aggregates
  if (selectedKind === 'model_suggestion' || selectedKind === 'action') {
    return undefined;
  }

  const rootName = selectedKind === 'category' ? 'topic' : selectedKind;
  const root =
    aggregates.find((a) => a.category_name === rootName) ?? findAggregateByCategoryName(aggregates, rootName);

  if (!root) return undefined;

  const nodes = findPathToLeaf(root, leafNames);
  if (!nodes || nodes.length < 2) return undefined;

  const leaf = nodes[nodes.length - 1]!;
  const immediateParent = nodes[nodes.length - 2]!;
  const destinationSteps = nodes.slice(0, -1);

  return { nodes, destinationSteps, leaf, immediateParent };
}

export function mapAggregateNodeToQueryAxis(node: ProjectAggregate): AggregateQueryAxis {
  const name = node.category_name;
  const isBuiltIn = BUILT_IN_AGGREGATE_CATEGORY_NAMES.has(name) || name in BUILTIN_GROUP_BY;

  if (isBuiltIn && BUILTIN_GROUP_BY[name]) {
    return {
      categoryName: name,
      groupBy: { kind: BUILTIN_GROUP_BY[name] } as GetInteractionAggregatesReqGroupBy,
      filterColumn: BUILTIN_FILTER_COLUMN[name] ?? name,
      moveKind: BUILTIN_MOVE_KIND[name] ?? name,
    };
  }

  return {
    categoryName: name,
    groupBy: { kind: 'custom_aggregate', custom_aggregate: name },
    filterColumn: `${CUSTOM_AGGREGATE_PREFIX}${name}`,
    moveKind: name,
  };
}

export type ResolvedActionMoveParents = {
  categoryKind: string;
  fromParentKind: string;
  toParentKind: string;
};

/**
 * Resolve move-categories parent/leaf kinds for an action move.
 * Falls back to the selected destination kind when no aggregate path is available
 * (e.g. model_suggestion, missing config).
 */
export type MoveFromParentContext = {
  intentFilters: Array<{ column: string; values?: string[] | null }>;
  parentName?: string | null;
  selectedProblem?: string | null;
  /** User-intents vertical tab (selected subtopic when not in URL filters). */
  verticalTabValue?: string | null;
};

/** Map a move-categories parent kind to the URL filter column that holds its value. */
export function resolveMoveFromParentFilterColumn(fromParentKind: string): string {
  if (fromParentKind in BUILTIN_FILTER_COLUMN) {
    return BUILTIN_FILTER_COLUMN[fromParentKind]!;
  }

  return `${CUSTOM_AGGREGATE_PREFIX}${fromParentKind}`;
}

/**
 * Resolve the source parent name for a move payload.
 * Must match `from_parent_kind` (e.g. sub_topic → category filter, not topic).
 */
export function resolveMoveFromParentName(fromParentKind: string, context: MoveFromParentContext): string | null {
  if (fromParentKind === 'model_suggestion') {
    return context.selectedProblem ?? null;
  }

  const filterColumn = resolveMoveFromParentFilterColumn(fromParentKind);
  const fromFilter = context.intentFilters.find((filter) => filter.column === filterColumn);
  const fromFilterValue = fromFilter?.values?.[0];
  if (fromFilterValue) return fromFilterValue;

  if (context.parentName) return context.parentName;

  if ((fromParentKind === 'sub_topic' || fromParentKind === 'category') && context.verticalTabValue) {
    return context.verticalTabValue;
  }

  return null;
}

/** Keep custom categories aligned with the ancestor path selected in progressive steps. */
export function filterCustomCategoriesByAncestorPath(
  categories: CategoryModel[],
  currentStep: ProjectAggregate,
  selectedPathValues: string[],
  destinationSteps: ProjectAggregate[],
): CategoryModel[] {
  const atCurrentStep = categories.filter((cat) => cat.name === currentStep.category_name);
  if (!selectedPathValues.length) return atCurrentStep;

  const byId = new Map(categories.map((cat) => [cat.id, cat]));

  return atCurrentStep.filter((cat) => {
    let node: CategoryModel | undefined = cat;

    for (let stepIndex = selectedPathValues.length - 1; stepIndex >= 0; stepIndex--) {
      const expectedStep = destinationSteps[stepIndex];
      const expectedValue = selectedPathValues[stepIndex];
      if (!expectedStep || !expectedValue || !node?.parent_id) return false;

      const parent = byId.get(node.parent_id);
      if (!parent) return false;
      if (parent.value !== expectedValue) return false;
      if (parent.name !== expectedStep.category_name) return false;

      node = parent;
    }

    return true;
  });
}

export function resolveActionMoveParentKinds(
  aggregates: ProjectAggregate[] | undefined,
  selectedKind: MoveDestinationKind,
): ResolvedActionMoveParents {
  const path = resolveAggregateMovePath(aggregates, selectedKind);

  if (!path) {
    const fallbackMoveKind =
      selectedKind === 'category' ? 'sub_topic' : selectedKind === 'action' ? 'action' : selectedKind;

    return {
      categoryKind: 'action',
      fromParentKind: fallbackMoveKind,
      toParentKind: fallbackMoveKind,
    };
  }

  const leafAxis = mapAggregateNodeToQueryAxis(path.leaf);
  const parentAxis = mapAggregateNodeToQueryAxis(path.immediateParent);

  return {
    categoryKind: leafAxis.moveKind,
    fromParentKind: parentAxis.moveKind,
    toParentKind: parentAxis.moveKind,
  };
}

export function getDestinationStepLabel(step: ProjectAggregate): string {
  return step.display_name || step.category_name;
}

/** Flat destination axis when no aggregate path is available (legacy / model_suggestion). */
export function fallbackQueryAxisForKind(selectedKind: MoveDestinationKind): AggregateQueryAxis {
  switch (selectedKind) {
    case 'topic':
      return mapAggregateNodeToQueryAxis({
        category_name: 'topic',
        display_name: 'topic',
        kind: 'topic',
      });
    case 'model_suggestion':
      return mapAggregateNodeToQueryAxis({
        category_name: 'model_suggestion',
        display_name: 'model_suggestion',
        kind: 'model_classes',
      });
    case 'action':
      return mapAggregateNodeToQueryAxis({
        category_name: 'action',
        display_name: 'action',
        kind: 'topic',
      });
    case 'category':
      return mapAggregateNodeToQueryAxis({
        category_name: 'sub_topic',
        display_name: 'sub_topic',
        kind: 'topic',
      });
  }
}
